import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ClientSearchModal from "../common/ClientSearchModal";
import FloatingInput from "../common/FloatingInput";
import TrueTextarea from "../common/TrueTextarea";
import { clientList, crisisReferrals } from "../services/soar";

function CrisisReferrals() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [additionalFilter, setAdditionalFilter] = useState("");
  const [beginDate, setBeginDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [client, setClient] = useState("");
  const navigate = useNavigate();

  const [crisisDatabase, setCrisisDatabase] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [referralToDeactivate, setReferralToDeactivate] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivateError, setDeactivateError] = useState("");

  const statuses = [
    "All",
    "New",
    "Pending",
    "Accepted",
    "Partially Accepted",
    "Not Dispatched",
    "Post Response",
    "Closed",
  ];

  const getReferrals = async () => {
    const resp = await crisisReferrals({
      command: "query",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      keyword: "viewall",
    });

    if (resp.code === "success" && resp.data) {
      setCrisisDatabase(resp.data);

      const clientIds = resp.data.map(
        (referralClient) => referralClient.clientId
      );

      const clientDataResp = await clientList({
        command: "query",
        environment:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "SOARQueryLive"
            : "SOARQueryBase",
        keyword: "byid",
        ids: clientIds,
        searchTerms: null,
      });
      setClientData(clientDataResp.data);
    }
  };

  useEffect(() => {
    getReferrals();
  }, []);

  useEffect(() => {
    if (client !== "") {
      const saveReferral = async (body) => {
        const resp = await crisisReferrals(body);
        if (resp.code === "success") {
          setClient("");
          navigate(`/crisis/${resp.id}`);
        }
      };

      const body = {
        command: "insert",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
            : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
        status: "New",
        clientId: client.contactid,
        clientName: client.fullname,
        clientFirstName: client.firstname,
        clientLastName: client.lastname,
        referralDateTime: new Date(),

        clientPhone: "",
        clientEmail: "",
        referralSource: "",
        serviceArea: "",
        addressLocation: "",
        locationNotes: "",
        respondersNeeded: 2,
        respondersAccepted: 0,
        notDispatchedReason: "",
        aware: false,
        personFacilitatingIntroduction: "",
        presentingConcern: "",
        cssrs: true,
        pointAssessment: true,
        pointAssessments: true,
        safetyPlan: true,
        crisisAssessment: true,
        hloc: true,
        resources: false,
        medicaid: "",
        dispatcher: "",
        location: "",
        goalOther: false,
        environmentalConcerns: false,
        gunsWeapons: false,
        animalsPresent: false,
        domesticViolence: false,
        aggression: false,
        suicidalBehavior: false,
        homicidalBehavior: false,
        psychosis: false,
        intoxication: false,
        withdrawal: false,
        safetyConcernsComments: "",
        reportedMedicalIssues: false,
        needMedicalAttention: false,
        illness: false,
        currentMedications: false,
        graveDisability: false,
        medicalComments: "",
        currentSubstanceUse: false,
        currentSubstanceAbuse: false,
        reportedSubstanceAbuse: false,
        needDetox: false,
        substanceUseComments: "",
        caseManager: false,
        licensedClinician: false,
        unlicensedClinician: false,
        peer: false,
        emt: false,
        otherResponder: false,
        secondaryResponderComments: "",
        lawEnforcement: false,
        childAdultProtectiveServices: false,
        emsFire: false,
        otherMentalHealth: false,
        others: false,
        professionalsOnSceneComments: "",
        idd: false,
        veteran: false,
        timeOfDay: "",
        weatherCondition: "",
        roadConditionsTraffic: "",
        facilityHomeCommunity: "",
        highCrimeArea: "",
        parkingScenarios: "",
        cellularConnectivity: false,
        internetConnectivity: false,
        entranceExitBarriers: false,
        entranceExitBarriersComments: "",
        walkingHazards: false,
        walkingHazardsComments: "",
        personsOutsideClient: false,
        personsOutsideClientComments: "",
        homeConditionHazards: false,
        homeConditionHazardsComments: "",
        weaponsInHome: "",
        weaponsInHomeComments: "",
        otherPersonsInHome: false,
        otherPersonsInHomeComments: "",
        petsHomeYard: false,
        petsHomeYardComments: "",
        wildfireNoxiousInsects: "",
        fleasBedBugsLiceTicksMites: "",
        infectiousDisease: false,
        infectiousDiseaseComments: "",
        entranceExitBarriersInside: false,
        entranceExitBarriersInsideComments: "",
        modifiedBy: JSON.parse(localStorage.getItem("safyInfo")).user.azureId,
      };

      saveReferral(body);
    }
  }, [client, navigate]);

  const filterData = useMemo(() => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

    const referrals = crisisDatabase
      ?.filter(
        (crisis) =>
          (!crisis.clientName ||
            crisis.clientName
              ?.toUpperCase()
              .includes(searchQuery.toUpperCase()) ||
            crisis.responders
              ?.map((r) => r.responderName)
              .join(", ")
              .toUpperCase()
              .includes(searchQuery.toUpperCase())) &&
          (filterType === "All" || crisis.status === filterType) &&
          (additionalFilter === "" ||
            (additionalFilter === "responder"
              ? crisis?.responders
                  ?.map((r) => r.responderName.toUpperCase())
                  .includes(safyInfo.user.displayName.toUpperCase())
              : additionalFilter === "creator"
              ? crisis.createdBy.toUpperCase() ===
                safyInfo.user.azureId.toUpperCase()
              : true)) &&
          (beginDate === "" ||
            new Date(crisis.referralDateTime) >= new Date(beginDate)) &&
          (endDate === "" ||
            new Date(crisis.referralDateTime) <=
              new Date(endDate).setDate(new Date(endDate).getDate() + 1))
      )
      .sort(
        (a, b) => new Date(b.referralDateTime) - new Date(a.referralDateTime)
      );

    return referrals;
  }, [
    crisisDatabase,
    searchQuery,
    filterType,
    additionalFilter,
    beginDate,
    endDate,
  ]);

  const filterCount = (status) => {
    return crisisDatabase?.filter(
      (crisis) =>
        crisis.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        crisis.status === status
    )?.length;
  };

  const statusColor = (status) => {
    switch (status) {
      case "New":
      case "Pending":
      case "Partially Accepted":
      case "Accepted":
        return "bg-safy-secondary text-white";
      case "Not Accepted":
      case "Not Dispatched":
        return "bg-danger text-white";
      case "Post Response":
        return "bg-warning";
      case "Closed":
        return "bg-secondary text-white";
      default:
        return "bg-safy-light";
    }
  };

  const forceDeactivate = async (referralId) => {
    const body = {
      command: "deactivate",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      id: referralId,
    };

    const resp = await crisisReferrals(body);
    if (resp.code === "success") getReferrals();
  };

  const deactivateReferral = async (referralId, reason) => {
    const query = {
      command: "query",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      keyword: "byid",
      ids: [referralId],
    };

    const queryResponse = await crisisReferrals(query);

    if (queryResponse.code === "success") {
      const latestReferralData = queryResponse.data[0];

      setCrisisDatabase((cd) => {
        const arr = [...cd];
        const referralIndex = arr.findIndex(
          (referral) => referral.id === latestReferralData.id
        );
        arr[referralIndex] = latestReferralData;
        return arr;
      });

      if (latestReferralData.responders?.length > 0) {
        confirmAlert({
          title: (
            <h5>
              <i className="fas fa-triangle-exclamation me-2 text-danger" />
              Action not permitted
            </h5>
          ),
          message:
            "The referral cannot be deactivated because a Responder has already been assigned",
          closeOnClickOutside: false,
          buttons: [
            {
              label: "Open Referral",
              onClick: () => {
                navigate(`/crisis/${latestReferralData.id}`);
              },
            },
            {
              label: "Close",
              onClick: () => {
                /* do nothing */
              },
            },
          ],
        });

        return;
      }
    } else {
      toast.error("An error occurred attempting to get latest referral info");
      return;
    }

    const body = {
      command: "deactivate",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      id: referralId,
      deactivateReason: reason,
    };
    const resp = await crisisReferrals(body);
    if (resp.code === "success") getReferrals();
  };

  const [viewportWidth, setViewportWidth] = useState(
    Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  );

  useEffect(() => {
    const getViewportWidth = () => {
      setViewportWidth(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      );
    };

    window.addEventListener("resize", getViewportWidth, true);
    return () => window.removeEventListener("resize", getViewportWidth);
  }, []);

  const clearDeactivateModal = () => {
    setDeactivateReason("");
    setDeactivateError("");
    setReferralToDeactivate(null);
  };

  return (
    <div>
      {referralToDeactivate ? (
        <Modal
          id="deactivate_modal"
          show={referralToDeactivate !== null}
          animation={false}
        >
          <Modal.Header className="bg-safy-info text-white">
            <Modal.Title>Delete Crisis Referral</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-safy2-4">
            <div className="fw-bold">{referralToDeactivate.clientName}</div>
            <div className="fw-bold mb-3">
              {moment(referralToDeactivate.referralDateTime).format("ll LT")}
            </div>
            <TrueTextarea
              name="deactivate_reason"
              label="Delete Reason"
              value={deactivateReason}
              onChange={({ currentTarget: input }) =>
                setDeactivateReason(input.value)
              }
              error={deactivateError}
            />
          </Modal.Body>
          <Modal.Footer className="bg-safy2-4">
            <button className="btn btn-danger" onClick={clearDeactivateModal}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (!deactivateReason) {
                  setDeactivateError("Reason must be provided");
                } else {
                  deactivateReferral(referralToDeactivate.id, deactivateReason);
                  clearDeactivateModal();
                }
              }}
            >
              Submit
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      <ClientSearchModal
        state="SAFY of Colorado"
        show={showClientSearch}
        setShow={setShowClientSearch}
        setClient={(id) => {
          setClient(id);
        }}
      />
      <div className="row justify-content-between">
        <div className="col-auto">
          <div className="h4">Crisis Referrals</div>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary me-2"
            onClick={() =>
              window.open(
                "msteams://teams.microsoft.com/l/entity/42f6c1da-a241-483a-a3cc-4f5be9185951"
              )
            }
          >
            Shifts
          </button>
          <div
            className="btn btn-success me-2"
            onClick={() => setShowClientSearch(true)}
          >
            New Referral
          </div>
        </div>
      </div>
      <div className="rounded border border-dark bg-light p-2 mt-4">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-4">
          <div className="col">
            <FloatingInput
              type="input"
              name="search"
              label="Search by Client or Responder Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {/* <SearchBox
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={"Search by Client Name"}
            /> */}
          </div>
          <div className="col">
            <FloatingInput
              type="select"
              name="additionalFilter"
              label="Additional Filter"
              value={additionalFilter}
              onChange={(e) => setAdditionalFilter(e.target.value)}
              options={[
                { name: "Referrals I'm a Responder", _id: "responder" },
                { name: "Referrals I Created", _id: "creator" },
              ]}
            />
          </div>
          <div className="col">
            <FloatingInput
              type="date"
              name="beginDate"
              label="Begin Date"
              value={beginDate}
              onChange={(e) => setBeginDate(e.target.value)}
            />
          </div>
          <div className="col">
            <FloatingInput
              type="date"
              name="endDate"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="row row-cols-2 row-cols-md-4 row-cols-lg-auto gy-2 mt-3">
          {statuses?.map((status, sindex) => (
            <div key={sindex} className="col">
              <button
                className={
                  filterType === status
                    ? "btn btn-sm btn-success"
                    : "btn btn-sm btn-safy-info"
                }
                onClick={() => {
                  setFilterType(status);
                }}
              >
                {status}{" "}
                {status === "All"
                  ? crisisDatabase?.length
                  : filterCount(status)}
              </button>
            </div>
          ))}
        </div>
      </div>

      {viewportWidth >= 768 ? (
        <>
          <div className="row mt-4">
            <div className="col fw-bold">Client</div>
            <div className="col fw-bold">Referral Date/Time</div>
            <div className="col fw-bold">Source</div>
            <div className="col fw-bold">Status</div>
            <div className="col fw-bold">Responders</div>
            <div className="col fw-bold">Actions</div>
          </div>
          {filterData?.map((crisis, index) => {
            const cd = clientData.find((c) => c.id === crisis.clientId);
            return (
              <div
                key={`referral-${crisis.id}-${index}`}
                className={`row rounded border border-dark card-safy ${statusColor(
                  crisis.status
                )} my-2 py-2`}
              >
                <div className="col">
                  {crisis.clientProgram ? (
                    <div
                      className="btn btn-primary border border-dark"
                      onClick={() => navigate(`/client/${crisis.clientId}`)}
                      title={
                        cd?.name
                          ? `${cd.name?.first} ${cd.name?.last}${
                              cd?.mpi ? `-${cd.mpi}` : ""
                            }`
                          : ""
                      }
                    >
                      {crisis.clientName}
                    </div>
                  ) : (
                    <div
                      className="fw-bold"
                      title={
                        cd?.name
                          ? `${cd.name?.first} ${cd.name?.last}${
                              cd?.mpi ? `-${cd.mpi}` : ""
                            }`
                          : ""
                      }
                    >
                      {crisis.clientName}
                    </div>
                  )}
                </div>
                <div className="col">
                  <div className="row row-cols-1 row-md-cols-2 justify-content-start gx-1">
                    <div className="col-auto">
                      {moment(crisis.referralDateTime).format("ll")}
                    </div>
                    <div className="col-auto">
                      {moment(crisis.referralDateTime).format("LT")}
                    </div>
                  </div>
                </div>
                <div className="col">{crisis.referralSource}</div>
                <div className="col">{crisis.status}</div>
                <div className="col">
                  {crisis.responders
                    ?.map((r) => r.responderName)
                    .sort()
                    .join(", ")}
                </div>
                <div className="col text-white">
                  {JSON.parse(localStorage.getItem("safyInfo")).user.azureId ===
                    "a9fb2d2d-6e73-4ecf-8b15-f375ebe32427" && (
                    <span
                      className="btn btn-danger me-2"
                      role="button"
                      onClick={() => forceDeactivate(crisis.id)}
                    >
                      Deactivate
                    </span>
                  )}
                  <Link
                    title="Open Crisis Referral"
                    to={`/crisis/${crisis.id}`}
                    onClick={() =>
                      window.scrollTo({ top: 0, left: 0, behavior: "instant" })
                    }
                  >
                    <i className="fas fa-pen text-white rounded border border-white bg-primary p-2 me-2" />
                  </Link>
                  {!crisis.clientProgram &&
                    (!crisis.responders || crisis.responders.length === 0) && (
                      <i
                        className="fas fa-trash-can rounded border border-white bg-danger p-2"
                        role="button"
                        onClick={() => setReferralToDeactivate(crisis)}
                      />
                    )}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="mt-4">
          {filterData?.map((crisis, index) => {
            const cd = clientData.find((c) => c.id === crisis.clientId);
            return (
              <div
                key={`referral-${crisis.id}-${index}`}
                className={`container rounded border border-dark card-safy ${statusColor(
                  crisis.status
                )} my-2 pt-2`}
              >
                <div className="row row-cols-2 row-cols-lg-4">
                  <div className="col">
                    <span className="me-2">Client:</span>
                    {crisis.clientProgram ? (
                      <div
                        className="btn btn-primary border border-dark"
                        onClick={() => navigate(`/client/${crisis.clientId}`)}
                        title={
                          cd?.name
                            ? `${cd.name?.first} ${cd.name?.last}${
                                cd?.mpi ? `-${cd.mpi}` : ""
                              }`
                            : ""
                        }
                      >
                        {crisis.clientName}
                      </div>
                    ) : (
                      <div
                        className="fw-bold"
                        title={
                          cd?.name
                            ? `${cd.name?.first} ${cd.name?.last}${
                                cd?.mpi ? `-${cd.mpi}` : ""
                              }`
                            : ""
                        }
                      >
                        {crisis.clientName}
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <div>Referral Date:</div>
                    <div className="fw-bold">
                      {moment(crisis.referralDateTime).format("L")}
                    </div>
                  </div>
                  {crisis.referralSource ? (
                    <div className="col">
                      <div>Referral Source:</div>
                      <div className="fw-bold">{crisis.referralSource}</div>
                    </div>
                  ) : null}
                  <div className="col">
                    <div>Referral Status:</div>
                    <div className="fw-bold">{crisis.status}</div>
                  </div>
                  <div className="col">
                    <div>Responders:</div>
                    <div className="fw-bold">
                      {crisis.responders
                        ?.map((r) => r.responderName)
                        .sort()
                        .join(", ")}
                    </div>
                  </div>
                </div>
                <div className="row justify-content-end border-top border-light gx-2 mt-2 py-2">
                  {JSON.parse(localStorage.getItem("safyInfo")).user.azureId ===
                    "a9fb2d2d-6e73-4ecf-8b15-f375ebe32427" && (
                    <div className="col-auto">
                      <button
                        className="btn btn-danger"
                        onClick={() => forceDeactivate(crisis.id)}
                      >
                        Force Deactivate
                      </button>
                    </div>
                  )}
                  <div className="col-auto">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/crisis/${crisis.id}`)}
                    >
                      Edit
                    </button>
                  </div>
                  {!crisis.clientProgram &&
                    (!crisis.responders || crisis.responders.length === 0) && (
                      <div className="col-auto">
                        <button
                          className="btn btn-danger"
                          onClick={() => setReferralToDeactivate(crisis)}
                        >
                          Deactivate
                        </button>
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CrisisReferrals;
